import { createRoot } from 'react-dom/client';
import { ThemeProvider } from './Components/BasicUI';
import * as Sentry from '@sentry/browser';
import * as dayjs from 'dayjs';
import timezonePlugin from 'dayjs/plugin/timezone';
import utcPlugin from 'dayjs/plugin/utc';

import { register } from './registerServiceWorker';
import { appConfiguration } from './AppConfiguration';
import './Services/firebaseApp';
import Redirect from './Navigation/Redirect';

import { RouterProvider, createRouter } from '@tanstack/react-router';
import { routeTree } from './routeTree.gen';
import ErrorPage from './Components/ErrorPage';

dayjs.extend(utcPlugin);
dayjs.extend(timezonePlugin);

const { version } = appConfiguration();
/**
 * NOTE: We are modifying previous Sentry behavior for improved dev experience.
 *
 * Currently, the `.env` file will have two **optional** values:
 *   VITE_SENTRY_DSN:
 *     The appropriate and valid public DSN for error reporting (or null).
 *   VITE_SENTRY_ENV:
 *     The environment to use when reporting an event. Environment should
 *     represent the execution context of the event, not the build type.
 *     for this reason, we cannot rely on `NODE_ENV` as we have multiple
 *     deployments that execute a "production" build in the different
 *     execution enviornments.
 * If the VITE_SENTRY_DSN is missing or empty, the SDK will not be initialized as
 * noted here:
 *   https://develop.sentry.dev/sdk/overview/#:~:text=SDKs%20should%20accept%20an%20empty%20DSN%20as%20valid%20configuration.
 * If the VITE_SENTRY_DSN is valid, but the VITE_SENTRY_ENV is
 * missing, we will fallback to the build type `NODE_ENV`, however, this is
 * considered an inappropriate configuration.
 */
const sentryDsn = import.meta.env.VITE_SENTRY_DSN || '';
const sentryEnv = import.meta.env.VITE_SENTRY_ENV || import.meta.env.MODE;
Sentry.init({
  dsn: sentryDsn,
  environment: sentryEnv,
  release: version,
  integrations: [
    Sentry.breadcrumbsIntegration({
      console: false,
    }),
  ],
});

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  scrollRestoration: true,
  defaultNotFoundComponent: () => (
    <ErrorPage
      code="404"
      title="Page Not Found"
      message="The page you are looking for does not exist or may have been moved."
    />
  ),
});

// Registration for typesafety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const rootElement = document.getElementById('root');

register();

if (rootElement !== null) {
  const root = createRoot(rootElement);
  root.render(
    <Redirect>
      <ThemeProvider>
        <RouterProvider router={router} />
      </ThemeProvider>
    </Redirect>,
  );
}
